/**
 * Ресурс cross-unit (L2) — типизированный клиент каталога кандидатных пар
 * (сценарии 6-7). Перенос с localhost:3009; отличается только транспортом:
 * вместо прямого fetch — платформенный `http` (правило 4 CLAUDE.md).
 *
 * МАРШРУТ тот же, что у остальных витрин аномалий:
 * `/api/ai/v1/analytics/anomalies?view=candidate-*` → gateway (маршрут ai) →
 * под transit-ai → сервис аномалий на claude-host → бэкенд кандидатных пар.
 * Ни компонент, ни хук не знают ни одного имени хоста.
 *
 * Каждая функция разворачивает конверт бэкенда и бросает CandidateApiError на
 * `status: 'ERROR'`. Благодаря этому «флаг выключен» (CROSS_UNIT_SCENARIOS_DISABLED)
 * и «бэкенд выключен» (CANDIDATE_API_DISABLED) видны как различимые ошибки, а не
 * как молча пустая таблица.
 *
 * ВАЖНО: конверт приходит и с кодом 4xx/5xx (например 503 при выключенном флаге).
 * `http` в таком случае бросает ApiError с распарсенным телом — достаём конверт
 * оттуда, иначе потеряли бы errorCode и показали бы бессмысленное «ошибка сети».
 */
import { http, ApiError } from '@/lib/api/http'
import type { QueryParams } from '@/lib/api/http'
import type {
  CandidateClusterData,
  CandidateClusterPairSort,
  CandidateClusterPairsData,
  CandidateCoverageData,
  CandidateEnvelope,
  CandidateExplanationsData,
  CandidatePairDetailData,
  CandidatePairListData,
  CandidatePairQuery,
  CandidatePairScoresData,
} from './cross-unit.types'
import { CANDIDATE_DEFAULT_PAGE_SIZE, CANDIDATE_MAX_PAGE_SIZE } from './cross-unit.types'
import { MAX_EVIDENCE_EXAMPLES } from '../evidence-row'
import type {
  EvidenceExamplesData,
  EvidenceRowDetailData,
} from './cross-unit.evidence-examples'
import type {
  CrossUnitCasesList,
  CrossUnitCasesQuery,
  CrossUnitCasesSummary,
  CrossUnitEligibilityAudit,
} from './cross-unit.user-view'
import { CROSS_UNIT_DEFAULT_PAGE_SIZE } from './cross-unit.user-view'

const BASE = '/api/ai/v1/analytics/anomalies'

// 401 от upstream-сервиса НЕ должен разлогинивать пользователя из платформы
// (сессии независимы) — как и в остальных ресурсах витрин аномалий.
const OPTS = { skipAuthRedirect: true, cache: 'no-store' as RequestCache }

export class CandidateApiError extends Error {
  readonly errorCode: string | null
  readonly component: string | null
  readonly httpStatus: number
  constructor(message: string, opts: { errorCode?: string | null; component?: string | null; httpStatus: number }) {
    super(message)
    this.name = 'CandidateApiError'
    this.errorCode = opts.errorCode ?? null
    this.component = opts.component ?? null
    this.httpStatus = opts.httpStatus
  }
}

/** Clamp mirrors the server clamp so an out-of-range page size never leaves the browser. */
export function clampClientPageSize(n: number | undefined): number {
  if (!Number.isFinite(n as number)) return CANDIDATE_DEFAULT_PAGE_SIZE
  return Math.min(Math.max(Math.trunc(n as number), 1), CANDIDATE_MAX_PAGE_SIZE)
}

/**
 * Отбрасывает пустые значения. `http.buildQuery` снимает только null/undefined,
 * а пустую строку отправляет как `key=` — исходный клиент такие ключи не слал.
 * Без этого «все уровни» (пустое значение селекта) уехало бы фильтром.
 */
function dropEmpty(params: QueryParams): QueryParams {
  const out: QueryParams = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v
  }
  return out
}

async function getCandidate<T>(params: QueryParams, signal?: AbortSignal): Promise<T> {
  let env: CandidateEnvelope<T> | null = null
  let httpStatus = 200
  try {
    env = await http.get<CandidateEnvelope<T>>(BASE, { ...OPTS, params: dropEmpty(params), signal })
  } catch (e) {
    if (!(e instanceof ApiError)) throw e
    httpStatus = e.status
    env = (e.body ?? null) as CandidateEnvelope<T> | null
  }
  if (!env || env.status !== 'OK' || env.data === null) {
    // SCORE_UNAVAILABLE / EXPLANATION_UNAVAILABLE приходят как status OK с данными —
    // это НЕ ошибки, их обрабатывают презентеры, а не этот слой.
    throw new CandidateApiError(env?.reason || `Кандидатный API недоступен (HTTP ${httpStatus})`, {
      errorCode: env?.errorCode,
      component: env?.component,
      httpStatus,
    })
  }
  return env.data
}

/* ── GET /api/v1/candidate/coverage ───────────────────────────────────────── */
export function fetchCandidateCoverage(): Promise<CandidateCoverageData> {
  return getCandidate<CandidateCoverageData>({ view: 'candidate-coverage' })
}

/* ── GET /api/v1/candidate/pairs ──────────────────────────────────────────── */
export function fetchCandidatePairs(q: CandidatePairQuery = {}): Promise<CandidatePairListData> {
  return getCandidate<CandidatePairListData>({
    view: 'candidate-pairs',
    page: Math.max(1, Math.trunc(q.page ?? 1)),
    page_size: clampClientPageSize(q.pageSize ?? CANDIDATE_DEFAULT_PAGE_SIZE),
    sort: q.sort,
    display_scenario_id: q.displayScenarioId,
    pairing_status: q.pairingStatus,
    is_existing_selected: q.isExistingSelected,
    is_group_representative: q.isGroupRepresentative,
    stable_case_group_id: q.stableCaseGroupId,
    track_a_min: q.trackAMin,
    track_a_max: q.trackAMax,
    track_a_level: q.trackALevel,
    track_a_relative_decile: q.trackARelativeDecile,
    anomaly_min: q.anomalyMin,
    anomaly_max: q.anomalyMax,
    // Три селектора подмножества аномалий взаимоисключающие: бэкенд отклоняет
    // запрос с несколькими сразу (CONFLICTING_ANOMALY_SUBSET_FILTERS), а не
    // выбирает победителя молча. http отбрасывает undefined, поэтому уходит
    // только активный.
    anomaly_level: q.anomalyLevel,
    analyst_review_eligible: q.analystReviewEligible,
    anomaly_level_in: q.anomalyLevelIn?.length ? q.anomalyLevelIn.join(',') : undefined,
    match_score_min: q.matchScoreMin,
    match_score_max: q.matchScoreMax,
    limited_history: q.limitedHistory,
    export_year: q.exportYear,
    include_baseline: q.includeBaseline,
  })
}

/* ── GET /api/v1/candidate/pairs/{canonical_pair_id} ──────────────────────── */
export function fetchCandidatePair(canonicalPairId: string): Promise<CandidatePairDetailData> {
  return getCandidate<CandidatePairDetailData>({
    view: 'candidate-pair',
    canonical_pair_id: canonicalPairId,
  })
}

/* ── GET /api/v1/candidate/pairs/{canonical_pair_id}/scores ───────────────── */
export function fetchCandidatePairScores(canonicalPairId: string): Promise<CandidatePairScoresData> {
  return getCandidate<CandidatePairScoresData>({
    view: 'candidate-pair-scores',
    canonical_pair_id: canonicalPairId,
  })
}

/* ── GET /api/v1/candidate/pairs/{canonical_pair_id}/explanations ─────────── */
export function fetchCandidatePairExplanations(canonicalPairId: string): Promise<CandidateExplanationsData> {
  return getCandidate<CandidateExplanationsData>({
    view: 'candidate-pair-explanations',
    canonical_pair_id: canonicalPairId,
  })
}

/* ── GET /api/v1/candidate/pairs/{id}/evidence/{key} (Stage 40) ───────────── */

/**
 * Up to 50 historical rows behind ONE evidence number.
 *
 * `limit` is sent as 50 and clamped again on the server: there is deliberately no way to
 * ask this client for the full set, and no pagination — the caption names the true total.
 */
export function fetchEvidenceExamples(
  canonicalPairId: string,
  evidenceKey: string,
  signal?: AbortSignal,
): Promise<EvidenceExamplesData> {
  return getCandidate<EvidenceExamplesData>({
    view: 'candidate-pair-evidence',
    canonical_pair_id: canonicalPairId,
    evidence_key: evidenceKey,
    limit: MAX_EVIDENCE_EXAMPLES,
  }, signal)
}

/* ── GET /api/v1/candidate/evidence-row (Stage 43) ────────────────────────── */

/**
 * Подробности ОДНОЙ строки примеров.
 *
 * Запрашивается только по явному действию — один запрос на открытую строку, ни одного при
 * отрисовке таблицы. `source_uid` уходит параметром запроса: в нём `|`, пробелы и
 * кириллица, и путь с ними переживает не каждый прокси цепочки.
 */
export function fetchEvidenceRowDetail(
  sourceUid: string,
  signal?: AbortSignal,
): Promise<EvidenceRowDetailData> {
  return getCandidate<EvidenceRowDetailData>({
    view: 'candidate-evidence-row',
    source_uid: sourceUid,
  }, signal)
}

/* ── GET /api/v1/candidate/pairs/{canonical_pair_id}/cluster ──────────────── */
export function fetchCandidatePairCluster(canonicalPairId: string): Promise<CandidateClusterData> {
  return getCandidate<CandidateClusterData>({
    view: 'candidate-pair-cluster',
    canonical_pair_id: canonicalPairId,
  })
}

/* ── GET /api/v1/candidate/clusters/{stable_case_group_id} ────────────────── */
export function fetchCandidateCluster(stableCaseGroupId: string): Promise<CandidateClusterData> {
  return getCandidate<CandidateClusterData>({
    view: 'candidate-cluster',
    stable_case_group_id: stableCaseGroupId,
  })
}

/* ── Stage 36: user-facing scenarios 6–7 ──────────────────────────────────── */
// Route eligibility, level counts, search, sorting and pagination are ALL resolved by the
// backend over the whole population. Nothing below ever fetches more than one page, and
// there is deliberately no "fetch all rows" function to reach for by mistake.

function userViewParams(q: CrossUnitCasesQuery): QueryParams {
  return {
    scenario_id: q.scenarioId === 'all' || q.scenarioId === undefined ? undefined : q.scenarioId,
    anomaly_level: q.anomalyLevel === 'all' ? undefined : q.anomalyLevel,
    ml_level: q.mlLevel === 'all' ? undefined : q.mlLevel,
    comparison_group: q.comparisonGroup === 'all' ? undefined : q.comparisonGroup,
    pairing_status: q.pairingStatus === 'all' ? undefined : q.pairingStatus,
    limited_history: q.limitedHistory ? 'true' : undefined,
    search: q.search,
  }
}

export function fetchCrossUnitCasesSummary(q: CrossUnitCasesQuery = {}): Promise<CrossUnitCasesSummary> {
  return getCandidate<CrossUnitCasesSummary>({
    view: 'candidate-user-summary',
    ...userViewParams(q),
  })
}

export function fetchCrossUnitCasesList(q: CrossUnitCasesQuery = {}): Promise<CrossUnitCasesList> {
  return getCandidate<CrossUnitCasesList>({
    view: 'candidate-user-rows',
    ...userViewParams(q),
    page: Math.max(1, Math.trunc(q.page ?? 1)),
    page_size: clampClientPageSize(q.pageSize ?? CROSS_UNIT_DEFAULT_PAGE_SIZE),
    sort_by: q.sortBy,
    sort_dir: q.sortDir,
  })
}

/** Eligibility audit (source / eligible / excluded + reasons). Diagnostics, not a filter. */
export function fetchCrossUnitEligibilityAudit(): Promise<CrossUnitEligibilityAudit> {
  return getCandidate<CrossUnitEligibilityAudit>({ view: 'candidate-user-audit' })
}

/* ── GET /api/v1/candidate/clusters/{stable_case_group_id}/pairs ──────────── */
export function fetchCandidateClusterPairs(
  stableCaseGroupId: string,
  opts: { page?: number; pageSize?: number; sort?: CandidateClusterPairSort } = {},
): Promise<CandidateClusterPairsData> {
  return getCandidate<CandidateClusterPairsData>({
    view: 'candidate-cluster-pairs',
    stable_case_group_id: stableCaseGroupId,
    page: Math.max(1, Math.trunc(opts.page ?? 1)),
    page_size: clampClientPageSize(opts.pageSize ?? CANDIDATE_DEFAULT_PAGE_SIZE),
    sort: opts.sort,
  })
}
